<template>
  <fieldset class="space-y-2" :data-testid="`rx-prescription-value-${name}`">
    <div class="flex items-center justify-between">
      <legend class="font-medium">
        <span class="text-xl">
          {{ $t(`rx.prescription.${name}.translatedName`) }}
        </span>
        <span class="ml-2 text-sm text-shade-400">
          ({{ $t(`rx.prescription.${name}.abbreviation`) }})
        </span>
      </legend>
      <div class="flex items-center gap-2">
        <span v-if="isOptional" class="text-sm text-shade-400">
          {{ $t('rx.prescription.rules.optionalStep.title') }}
        </span>
        <FimButton
          v-if="isOverlayAvailable"
          type="raw"
          no-padding
          class="text-shade-400 hover:text-shade-600"
          :label="overlayTitle"
          :data-testid="`rx-prescription-value-${step}-overlay`"
          @click="showOverlay"
        >
          <LazyIconFielmannInfoCircle class="size-5" />
        </FimButton>
      </div>
    </div>
    <div class="">
      <component
        :is="componentIs"
        :id="`prescription-${step}-right-value`"
        class="mb-4"
        :data-testid="`rx-prescription-value-${step}-right`"
        :value="rightValue"
        :options="options"
        :placeholder="$i18n.t('rx.prescription.inputPlaceholder.right')"
        :placeholder-before-value="0"
        :unit="unit"
        :min="min"
        :max="max"
        :step="step"
        :is-valid="isRightValid"
        @update:value="setRight"
      />
      <component
        :is="componentIs"
        :id="`prescription-${step}-left-value`"
        :data-testid="`rx-prescription-value-${step}-left`"
        :value="leftValue"
        :options="options"
        :placeholder="$i18n.t('rx.prescription.inputPlaceholder.left')"
        :placeholder-before-value="0"
        :unit="unit"
        :min="min"
        :max="max"
        :step="step"
        :is-valid="isLeftValid"
        @update:value="setLeft"
      />
    </div>

    <InlineMessage
      v-if="warningText"
      type="warning"
      :description="warningText"
    />

    <div
      v-if="usePdTool"
      class="!mt-4 space-y-4 rounded-2xl bg-shade-200 px-6 py-4"
    >
      <div v-if="!pdToolSuccess" class="space-y-2">
        <div class="flex items-center justify-start">
          <BadgeFtPrismaDesign
            type="default"
            :label="$t('badge_labels.new')"
            size="small"
            class="mr-3"
          />
          <p class="font-medium">{{ $t('rx.prescription.pd.toolHelpText') }}</p>
        </div>
      </div>
      <InlineMessage
        v-if="pdToolSuccess"
        type="success"
        :description="$t('rx.prescription.pd.toolSuccess')"
      />
      <PdTool
        v-else
        btn-class="w-full"
        btn-id="rx-configurator-step-3-pd"
        :show-header-and-backdrop="true"
        @pd:change="onPdToolChange"
      />
    </div>
  </fieldset>

  <Overlay
    v-if="isOverlayAvailable"
    :title="overlayTitle"
    :open="isOverlayOpen"
    layout="right"
    enter-from="right"
    @update:open="isOverlayOpen = false"
  >
    <div v-for="content in overlayContent" :key="content._uid">
      <component :is="content.component" :blok="content" />
    </div>
  </Overlay>
</template>

<script setup lang="ts">
import type {
  IOption,
  ISightPrescriptionUnit,
  IValidatorResponse,
} from '~/components/rx/RxConfigurator/RxConfigurator'
import { FimSelect, FimInput } from '#components'

const props = defineProps({
  name: {
    type: String as PropType<IPrescriptionProp>,
    required: true,
  },
  unit: {
    type: String as PropType<ISightPrescriptionUnit>,
    required: true,
  },
  storyblokSlug: {
    type: String as PropType<string>,
    required: true,
  },
  validate: {
    type: Function as PropType<
      (
        state: IPrescriptionValues,
        left?: number,
        right?: number,
      ) => IValidatorResponse
    >,
    required: true,
  },
  options: {
    type: Array as PropType<IOption[]>,
    required: false,
    default: undefined,
  },
  min: {
    type: Number as PropType<number>,
    default: undefined,
  },
  max: {
    type: Number as PropType<number>,
    default: undefined,
  },
  isOptional: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  step: {
    type: Number as PropType<number>,
    default: undefined,
  },
})

defineEmits(['click', 'back'])

const $i18n = useI18n()
const { trackNotification } = await useTrackingEvents()
const componentIs = computed(() => (props.options ? FimSelect : FimInput))
const scope = effectScope()
onScopeDispose(() => scope.stop())

const { prescriptionValues, getPrescriptionPropSetters } = useRxPrescription()
const { setLeft, setRight } = getPrescriptionPropSetters(props.name)

const validatePrescriptionValue = (
  value: number | string | undefined,
): string | number => {
  return isDefined(value) ? value : ''
}

const leftValue = computed(() =>
  validatePrescriptionValue(currentStepVals.value?.left),
)
const rightValue = computed(() =>
  validatePrescriptionValue(currentStepVals.value?.right),
)

/** validation */

const currentStepVals = computed(() => prescriptionValues.value[props.name])
const validatorResponse = computed(() =>
  props.validate(
    prescriptionValues.value,
    currentStepVals.value?.left,
    currentStepVals.value?.right,
  ),
)

onBeforeMount(() => {
  if (!validatorResponse.value.isLeftValid) {
    setLeft(EMPTY_PRESCRIPTION_VALUE.left)
  }
  if (!validatorResponse.value.isRightValid) {
    setRight(EMPTY_PRESCRIPTION_VALUE.left)
  }
})

const isLeftValid = computed(() => validatorResponse.value.isLeftValid)
const isRightValid = computed(() => validatorResponse.value.isRightValid)

const warningText = computed(() =>
  validatorResponse.value.warningText
    ? $i18n.t(validatorResponse.value.warningText)
    : '',
)

scope?.run(() => {
  if (import.meta.server) {
    return
  }
  watch(warningText, () => {
    if (warningText.value) {
      trackNotification(warningText.value)
    }
  })
})

/** PD tool */

const pdToolSuccess = ref(false)
const { isPdToolEnabled } = useFeatureToggles()

const usePdTool = computed(() => isPdToolEnabled() && props.name === 'pd')

const onPdToolChange = (data: { left: number; right: number }) => {
  pdToolSuccess.value = Boolean(data.left) || Boolean(data.right)

  if (data.left) {
    setLeft(data.left)
  }
  if (data.right) {
    setRight(data.right)
  }

  trackNotification($i18n.t('rx.prescription.pd.toolSuccess'))
}

/** overlay */

const {
  overlayTitle,
  overlayContent,
  available: isOverlayAvailable,
  open: isOverlayOpen,
  show: showOverlay,
} = await useOverlay(props.storyblokSlug)
</script>
